import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomModal,
  CustomSelect,
  CustomTextInput,
  CustomMaskInput,
  Option,
  Text,
  useText,
} from '@src/components';
import { Form, Input } from 'antd';
import modalClose from '@src/assets/icons/icon-close.svg';
import '@src/styles/companyInformation/deliveryAdresses/deliveryAddressAddModal.scss';
import { turkishToLower } from '@src/utils/utils';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cityGetAll, countyGetAll, neighborhoodGetAll } from '@src/store/slice/lookupSlice';
import { addressSave, addressUpdate } from '@src/store/slice/addressSlice';
import PropTypes from 'prop-types';
import { formPhoneRegex } from '@src/utils/formRule';

const { TextArea } = Input;
const DeliveryAddressAddModal = ({ modalVisible, handleModalVisible, selectedRow }) => {
  const { cityList, countyList, neighborhoodList } = useSelector((state) => state?.lookup);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [isDisable, setIsDisable] = useState(false);

  useEffect(() => {
    form.resetFields();
  }, [modalVisible])

  useEffect(() => {
    if (modalVisible && cityList.length === 0) {
      (async () => {
        await dispatch(cityGetAll());
      })();
    }
  }, [cityList.length, dispatch, modalVisible]);

  useEffect(() => {
    if (modalVisible && selectedRow) {
      const editables = {
        ...selectedRow,
        cityId: selectedRow?.cityId,
        countyId: selectedRow?.countyName,
        neighborhoodId: selectedRow?.neighborhoodName,
      };
      form.setFieldsValue(editables);
    }
  }, [modalVisible]);

  const cityChange = useCallback(
    async (cityId) => {
      try {
        await dispatch(countyGetAll(cityId));
        form.resetFields(['countyId', 'neighborhoodId']);
        setIsDisable(true);
      } catch (e) { }
    },
    [dispatch],
  );

  const countyChange = useCallback(
    async (countyId) => {
      try {
        await dispatch(neighborhoodGetAll(countyId));
        form.resetFields(['neighborhoodId']);
        setIsDisable(false);
      } catch (e) { }
    },
    [dispatch],
  );

  const handleClose = useCallback(() => {
    handleModalVisible(false);
    form.resetFields();
  }, [form, handleModalVisible]);

  const onAdd = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (typeof values?.countyId === 'string') values.countyId = selectedRow?.countyID;
      if (typeof values?.neighborhoodId === 'string') values.neighborhoodId = selectedRow?.neighborhoodId;
      const body = {
        ...values,
        receiverPhone: values?.receiverPhone
          .replace(/\)/g, '')
          .replace(/\(/g, '')
          .replace(/-/g, '')
          .replace(/ /g, ''),
      };
      dispatch(addressSave(body));
    } catch (e) { }
  }, [form, dispatch, selectedRow]);

  const update = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (typeof values?.countyId === 'string') values.countyId = selectedRow?.countyId;
      if (typeof values?.neighborhoodId === 'string')
        values.neighborhoodId = selectedRow?.neighborhoodId;
      const body = {
        ...values,
        id: selectedRow?.id,
        receiverPhone: values?.receiverPhone
          .replace(/\)/g, '')
          .replace(/\(/g, '')
          .replace(/-/g, '')
          .replace(/ /g, ''),
      };
      dispatch(addressUpdate(body));
    } catch (e) { }
  }, [form, dispatch, selectedRow]);

  const onSave = useCallback(async () => {
    if (selectedRow) {
      update();
    } else {
      onAdd();
    }
  }, [onAdd, update, selectedRow]);

  return (
    <CustomModal
      className="delivery-address-add delivery-address-add-modal"
      width={838}
      maskClosable={false}
      footer={
        <div className={'footer-content'}>
          <CustomButton id="companyInformation-deliveryAddress-deliveryAddressAddModal-save-btn" type="primary" onClick={onSave} className={'save-button'}>
            <Text t="add" />
          </CustomButton>
        </div>
      }
      title={selectedRow ? <Text t="editAddress" /> : <Text t="newDeliveryAddress" />}
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={<CustomImage src={modalClose} />}
    >
      <CustomForm
        name="deliveryAddressAddForm"
        initialValues={{}}
        autoComplete="off"
        layout={'vertical'}
        form={form}
      >
        <div className="row ">
          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="deliveryAddressTitle" />}
              name="addressName"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomInput
                placeholder={useText('deliveryAddressTitle')}
                id="companyInformation-deliveryAddress-deliveryAddressAddModal-addressName-input"
                height={48} />
            </CustomFormItem>
          </div>

          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="recipient" />}
              name="receiverName"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomTextInput
                placeholder={useText('recipient')}
                id="companyInformation-deliveryAddress-deliveryAddressAddModal-receiverName-input"
                height={48} />
            </CustomFormItem>
          </div>
          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="phoneNumber" />}
              name="receiverPhone"
              className={'delivery-address-add-form-item'}
              rules={[
                { required: true, message: <Text t="requiredField2" /> },
                { validator: formPhoneRegex, message: <Text t="enterValidPhoneNumber" /> },
              ]}
            >
              <CustomMaskInput id="companyInformation-deliveryAddress-deliveryAddressAddModal-receiverPhone-maskInput" mask={'+\\90 (999) 999 99 99'}>
                <CustomInput id="companyInformation-deliveryAddress-deliveryAddressAddModal-receiverPhone-input" placeholder={useText('+90 (___) ___ __ __')} height={48} />
              </CustomMaskInput>
            </CustomFormItem>
          </div>
          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="province" />}
              name="cityId"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomSelect
                id="companyInformation-deliveryAddress-deliveryAddressAddModal-cityId-select"
                showSearch
                height={48}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  turkishToLower(option.children).includes(turkishToLower(input))
                }
                placeholder={useText('province')}
                onChange={cityChange}
              >
                {cityList.map((item) => {
                  return (
                    <Option id={`companyInformation-deliveryAddress-deliveryAddressAddModal-cityId-${item?.name}-option`} key={item?.id} value={item?.id}>
                      {item?.name}
                    </Option>
                  );
                })}
              </CustomSelect>
            </CustomFormItem>
          </div>

          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="district" />}
              name="countyId"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomSelect
                id="companyInformation-deliveryAddress-deliveryAddressAddModal-countyId-select"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  turkishToLower(option.children).includes(turkishToLower(input))
                }
                height={48}
                placeholder={useText('district')}
                disabled={countyList.length === 0}
                onChange={countyChange}
              >
                {countyList.map((item) => {
                  return (
                    <Option id={`companyInformation-deliveryAddress-deliveryAddressAddModal-countyId-${item?.name}-option`} key={item?.id} value={item?.id}>
                      {item?.name}
                    </Option>
                  );
                })}
              </CustomSelect>
            </CustomFormItem>
          </div>

          <div className="col-md-4">
            <CustomFormItem
              label={<Text t="neighborhood" />}
              name="neighborhoodId"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomSelect
                id="companyInformation-deliveryAddress-deliveryAddressAddModal-neighborhoodId-select"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  turkishToLower(option.children).includes(turkishToLower(input))
                }
                height={48}
                placeholder={useText('neighborhood')}
                disabled={neighborhoodList.length === 0 || isDisable}
              >
                {neighborhoodList.map((item) => {
                  return (
                    <Option id={`companyInformation-deliveryAddress-deliveryAddressAddModal-neighborhoodId-${item?.name}-option`} key={item?.id} value={item?.id}>
                      {item?.name}
                    </Option>
                  );
                })}
              </CustomSelect>
            </CustomFormItem>
          </div>

          <div className="col-md-12">
            <CustomFormItem
              label={<Text t="openAddress" />}
              name="detail"
              className={'delivery-address-add-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <TextArea
                placeholder={useText('openAddress')}
                id="companyInformation-deliveryAddress-deliveryAddressAddModal-detail-textArea"
                rows={3} />
            </CustomFormItem>
          </div>
        </div>
      </CustomForm>
    </CustomModal>
  );
};

DeliveryAddressAddModal.propTypes = {
  modalVisible: PropTypes.bool,
  handleModalVisible: PropTypes.func,
  selectedRow: PropTypes.any
};
export default DeliveryAddressAddModal;
